import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags';
import { useStringFeatureFlag } from '@confluence/session-data';

import { MACRO_FALLBACK_UI_FF } from '../constants';

const i18n = defineMessages({
	fallbackFlagTitle: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.title',
		defaultMessage: 'This element may require you to publish',
		description: 'The title of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagDescription: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.description',
		defaultMessage:
			'To fully display and use this element, publish your page via the <menuIcon></menuIcon> menu on the toolbar.',
		description: 'The body of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagLearnMoreAction: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.action',
		defaultMessage: 'Learn more about this EAP',
		description: 'The link to learn more on the fallback flag when an unsupported macro is added',
	},
});

export const useMacroFallbackFlag = (flags: FlagsStateContainer, isLivePage: Boolean) => {
	const macrosToShowFallbackUi = useStringFeatureFlag(MACRO_FALLBACK_UI_FF);
	const intl = useIntl();

	const showMacroFallbackFlag = useCallback(
		async ({
			macro,
			isBodiedExtension,
		}: {
			macro: string | undefined;
			isBodiedExtension: boolean;
		}) => {
			const allowAll = macrosToShowFallbackUi === 'ALLOW_ALL';
			if (!macrosToShowFallbackUi || !macro || !isLivePage || allowAll) {
				return;
			}

			const macros = macrosToShowFallbackUi.split(',').map((m) => m.trim().toLowerCase());
			const macroKey = macro.toLowerCase();

			const is2PNonBodied = !isMacro1P(macroKey) && !isBodiedExtension;
			if (is2PNonBodied) {
				return;
			}

			// If macro is in the allowlist, or allowed barring a special case, don't show the warning
			if (
				macros.includes(macroKey) ||
				(macroKey === 'create-from-template' &&
					fg('confluence_frontend_page_experiences_cft_wysiwig')) ||
				(macroKey === 'livesearch' && fg('cc_page_experiences_live_search_wysiwyg')) ||
				(macroKey === 'anchor' && fg('cc_page_experiences_anchor_macro_refresh'))
			) {
				return;
			}

			await flags.showFlag({
				type: 'warning',
				close: 'auto',
				title: intl.formatMessage(i18n.fallbackFlagTitle),
				description: intl.formatMessage(i18n.fallbackFlagDescription, {
					menuIcon: () => <MoreIcon LEGACY_size="small" label="Icon for publish menu" />,
				}),
				// TODO: Change this to the correct link when available
				// actions: [
				//   {
				//     content: intl.formatMessage(i18n.fallbackFlagLearnMoreAction),
				//     href: "#",
				//   },
				// ],
			});
		},
		[flags, macrosToShowFallbackUi, intl, isLivePage],
	);

	return { showMacroFallbackFlag };
};

const isMacro1P = (macro: string) => MACROS_1P.has(macro);

const MACROS_1P = new Set<string>([
	'toc',
	'children',
	'livesearch',
	'recently-updated',
	'details',
	'jlol',
	'jira',
	'pagetree',
	'anchor',
	'contentbylabel',
	'create-from-template',
	'roadmap',
	'blog-posts',
	'excerpt',
	'attachments',
	'profile',
	'include',
	'content-report-table',
	'detailssummary',
	'tasks-report-macro',
	'excerpt-include',
	'iframe',
	'listlabels',
	'profile-picture',
	'change-history',
	'pagetreesearch',
	'widget',
	'decisionreport',
	'viewpdf',
	'contributors',
	'gadget',
	'popular-labels',
	'jirachart',
	'toc-zone',
	'portfolioforjiraplan',
	'viewppt',
	'viewxls',
	'recently-updated-dashboard',
	'spaces',
	'index',
	'contributors-summary',
	'userlister',
	'gallery',
	'viewdoc',
	'chart',
	'related-labels',
	'jiraroadmap',
]);
